import type { HTMLAttributes, ReactNode } from 'react';

import { buildClassName } from '@utils';

import './Card.css';

export type CardElevation = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Card elevation level (shadow depth)
   * @default 2
   */
  elevation?: CardElevation;
  /**
   * Whether card is interactive (hover effects)
   * @default false
   */
  interactive?: boolean;
  /**
   * Whether card has padding
   * @default true
   */
  padding?: boolean;
  /**
   * In dark mode, use a dark surface (e.g. gray-800) instead of white. Use for sections that need dark-mode-friendly cards.
   * @default false
   */
  darkSurface?: boolean;
  /**
   * Card content
   */
  children: ReactNode;
}

export function Card({
  elevation = 2,
  interactive = false,
  padding = true,
  darkSurface = false,
  className = '',
  children,
  ...props
}: CardProps) {
  const cardClasses = buildClassName(
    'bhg-card',
    `bhg-card--elevation-${elevation}`,
    interactive && 'bhg-card--interactive',
    !padding && 'bhg-card--no-padding',
    darkSurface && 'bhg-card--dark-surface',
    className
  );

  return (
    <div className={cardClasses} {...props}>
      {children}
    </div>
  );
}
